import React, { useContext } from "react";

import Grid from "@mui/material/Grid";
import ClinicListContainer from "./containers/ClinicListContainer";
import ClinicUserListContainer from "./containers/ClinicUserListContainer";
import ClinicModalForm from "./components/ClinicModalForm";
import ConfirmModal from "components/ConfirmModal";
import { Button } from "components/Buttons";
import StyledTab from "./components/Tab";
import AddClinicAdminModalForm from "./components/AddClinicAdminModalForm";

import { selectClinicList } from "store/ClinicStore/selectors";
import { getClinicList, getClinicUserList } from "store/ClinicStore/actions";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { ClinicType } from "types/Clinic";
import { ClinicRepository } from "repositories/ClinicRepository";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { SortDirection, Option, StatusENUM } from "types/common";
import { resetPagination } from "store/ClinicStore/clinicSlice";
import { AuthContext } from "../../contexts/authContext";
import { USER_GROUPS } from "../../constants/roles";

const TABS = {
  clinics: "clinic",
  users: "users",
} as const;

type SelectedTabType = typeof TABS[keyof typeof TABS];

const ClinicOverview = () => {
  const dispatch = useAppDispatch();
  const clinicList = useAppSelector(selectClinicList);
  const authContext = useContext(AuthContext);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [editableItem, setEditableItem] = React.useState<ClinicType>();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<SelectedTabType>(
    TABS.clinics
  );
  const [search, setSearch] = React.useState("");
  const [clinicOptions, setClinicOptions] = React.useState<Option[]>([]);
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = React.useState(false);
  const [addingAdminClinicId, setAddingAdminClinicId] = React.useState("");
  const [disablingUser, setDisablingUser] = React.useState<{
    userId: string;
    status?: StatusENUM;
  } | null>(null);
  const [disablingClinic, setDisablingClinic] = React.useState<{
    clinicId?: string;
    status?: StatusENUM;
  } | null>(null);
  const clinicRepository = ClinicRepository.getInstance();

  const firstRender = React.useRef(true);

  const confirmModalContent = React.useMemo(() => {
    if (
      activeTab === TABS.users &&
      disablingUser?.status === StatusENUM.disabled
    ) {
      return "Do you want to reactivate this user?";
    }

    return `
      Are you sure you want to disable ${
        activeTab === TABS.clinics
          ? "the clinic? All the users within the clinic organization will be disabled as well."
          : "this user?"
      }
      `;
  }, [activeTab, disablingUser]);

  const editClinic = (clinicId: string) => {
    const clinic = clinicList.find(({ _id }) => _id === clinicId);
    setEditableItem(clinic);
    setIsFormOpen(true);
  };

  const disableClinic = async (clinicId: string, status: StatusENUM) => {
    setDisablingClinic({ clinicId, status });
    setIsConfirmModalOpen(true);
  };

  const disableUser = async (userId: string, status?: StatusENUM) => {
    setDisablingUser({ userId, status });
    setIsConfirmModalOpen(true);
  };

  const resendInvitation = async (email: string, role: any) => {
    console.log(role);
    await authContext.reSendClinicInvite(email, role, true);
    getClinicUserData();
  };

  const onTabsChange = (tab: SelectedTabType) => {
    if (tab === activeTab) {
      return;
    }

    dispatch(resetPagination());

    setActiveTab(tab);

    if (tab === TABS.clinics) {
      return getClinicData();
    }

    getClinicUserData();
  };

  const onAcceptConfirmModal = async () => {
    if (activeTab === TABS.clinics) {
      if (disablingClinic?.status === StatusENUM.disabled) {
        await clinicRepository.enableClinic(disablingClinic?.clinicId);
      } else {
        await clinicRepository.disableClinic(disablingClinic?.clinicId);
      }
      setDisablingClinic({ clinicId: "" });
      getClinicData();
    } else {
      if (!disablingUser?.userId) {
        return;
      }
      if (disablingUser?.status === StatusENUM.disabled) {
        await clinicRepository.enableClinicianFromCognito(
          disablingUser?.userId
        );
      } else {
        await clinicRepository.disableClinicianFromCognito(
          disablingUser?.userId
        );
      }
      setDisablingUser(null);
      getClinicUserData();
    }

    setIsConfirmModalOpen(false);
  };

  const onConfirmClose = () => {
    setDisablingClinic({ clinicId: "" });
    setIsConfirmModalOpen(false);
  };

  const closeFormModal = () => {
    setIsFormOpen(false);
    setIsAddAdminModalOpen(false);
    setEditableItem(undefined);
    setAddingAdminClinicId("");
    setSearch("");
  };

  const addClinicAdmin = (clinicId: string) => {
    setIsAddAdminModalOpen(true);
    setAddingAdminClinicId(clinicId);
  };

  const getClinicData = React.useCallback(() => {
    dispatch(resetPagination());
    dispatch(getClinicList());
  }, [dispatch]);

  const getClinicUserData = React.useCallback(() => {
    dispatch(resetPagination());
    dispatch(getClinicUserList());
  }, [dispatch]);

  const getClinicAutocompleteList = async () => {
    const clinicRepository = ClinicRepository.getInstance();
    const clinicList = await clinicRepository.getClinicList({
      limit: 20,
      offset: 0,
      search,
      sortBy: "name",
      sortDirection: SortDirection.asc,
    });

    setClinicOptions(
      clinicList.items.map((clinic) => ({
        label: clinic.name,
        value: clinic._id,
      }))
    );
  };

  const onCreateClinicClick = () => {
    setIsFormOpen(true);
  };

  const onCreateUserClick = () => {
    setAddingAdminClinicId("");
    setIsAddAdminModalOpen(true);
    getClinicAutocompleteList();
  };

  const onClinicChange = (value: string) => {
    setSearch(value);
  };

  React.useEffect(() => {
    getClinicData();
  }, []);

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    getClinicAutocompleteList();
  }, [search]);

  return (
    <>
      <div className="container max-w-[90%] m-auto pt-[40px]">
        <h1 className="leading-10">
          Hi, Super Admin.
          <br />
          Here is your {activeTab === TABS.clinics ? "Clinics" : "Users"}{" "}
          overview.
        </h1>

        {
          <section className="w-full mt-8">
            <Grid item container flexDirection="column">
              <Grid container justifyContent="space-between">
                <Grid item md={2} container gap="10px" wrap="nowrap" mb={3}>
                  <StyledTab
                    isSelected={activeTab === TABS.clinics}
                    onClick={() => onTabsChange(TABS.clinics)}
                  >
                    Clinics
                  </StyledTab>

                  <StyledTab
                    isSelected={activeTab === TABS.users}
                    onClick={() => onTabsChange(TABS.users)}
                  >
                    Users
                  </StyledTab>
                </Grid>

                <Grid item>
                  <Button
                    title={`Create ${
                      activeTab === TABS.clinics ? "Clinic" : "User"
                    } `}
                    onClick={
                      activeTab === TABS.clinics
                        ? onCreateClinicClick
                        : onCreateUserClick
                    }
                    type={ButtonType.normal}
                    colorScheme={ButtonColorScheme.gradient}
                    shadowed
                  />
                </Grid>
              </Grid>
              <h3>{`${
                activeTab === TABS.clinics ? "Clinic" : "User"
              } List`}</h3>
            </Grid>

            {activeTab === TABS.clinics ? (
              <ClinicListContainer
                disableClinic={disableClinic}
                editClinic={editClinic}
                addClinicAdmin={addClinicAdmin}
              />
            ) : (
              <ClinicUserListContainer
                disableUser={disableUser}
                resendInvitation={resendInvitation}
                editUser={editClinic}
              />
            )}
          </section>
        }
      </div>

      <ClinicModalForm
        isOpen={isFormOpen}
        closeModal={closeFormModal}
        clinic={editableItem}
        getList={getClinicData}
        addClinicAdmin={addClinicAdmin}
      />

      <AddClinicAdminModalForm
        isOpen={isAddAdminModalOpen}
        clinicId={addingAdminClinicId}
        clinicOptions={clinicOptions}
        closeModal={closeFormModal}
        getList={activeTab === TABS.users ? getClinicUserData : getClinicData}
        onClinicChange={onClinicChange}
      />

      <ConfirmModal
        title={`${
          disablingUser?.status === StatusENUM.disabled
            ? "Reactivate"
            : "Disable"
        } ${activeTab === TABS.clinics ? "Clinic" : "User"} `}
        open={isConfirmModalOpen}
        onAccept={onAcceptConfirmModal}
        onClose={onConfirmClose}
        content={confirmModalContent}
        acceptButtonText="Yes"
        cancelButtonText="No"
      />
    </>
  );
};

export default ClinicOverview;
